import { useCallback, useEffect, useRef, useState } from "react";
import { proximaCor, proximaPosicaoLivre, type AnotacaoRapida, type CorDoMural } from "@noesis/shared";
import { FalhaDaApi, pedir } from "../api.js";
import { Icone } from "../componentes/Icone.js";
import { IconeCriar, IconeExcluir, IconeSeguir, IconeVoltar } from "../componentes/icones.js";

/**
 * O mural de anotações rápidas — RN-50.
 *
 * **Duas formas, uma só lista** (RN-30). Em tela larga os papéis são
 * arrastáveis e a posição é gravada; em tela estreita não há arrasto e a
 * reordenação é ação explícita, com dois botões. Não é preferência: arrastar
 * disputa o gesto de rolagem no celular, e perder a rolagem numa tela que rola é
 * pior do que não poder reposicionar um papel.
 *
 * Qual das duas vale é decidido por `matchMedia` aqui, e não por CSS, porque a
 * diferença não é aparência: no largo existem ouvintes de ponteiro, no estreito
 * não existem. Montar os dois e esconder um deixaria o arrasto ativo por baixo
 * da lista.
 *
 * **A gravação da posição é por debounce.** Arrastar dispara evento contínuo, e
 * uma requisição por movimento é defeito — a regra diz isso em letra. O que sai
 * é a posição final da parada, e ela vai numa rota própria: mandar o texto junto
 * a cada movimento sobrescreveria o que estivesse sendo digitado em outra aba.
 *
 * **A cor é sorteada na criação e não se escolhe** (ADR 018). Ela girava por
 * `proximaCor` no papel novo *e* tinha quatro alvos de cor no rodapé de cada
 * papel — um quarto da área do papel gasta com uma decisão de enfeite, numa
 * anotação cujo ponto é ser escrita antes de a ideia fugir. Os quatro alvos
 * saíram; o giro ficou.
 */

const LARGO = "(min-width: 64rem)";

/**
 * `parede` liga o mural livre, com papéis soltos. No Início do Caderno o
 * mural mora numa coluna de 360px, e ali ele é lista (ADR 040).
 */
export function Mural({ parede: comParede = false }: { parede?: boolean } = {}) {
  const [anotacoes, setAnotacoes] = useState<AnotacaoRapida[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [criando, setCriando] = useState(false);
  const [largoDaTela, setLargo] = useState(() => window.matchMedia(LARGO).matches);
  const largo = comParede && largoDaTela;
  // A referência mora aqui, e não só dentro da parede: quem cria o papel precisa
  // medir a parede antes de mandar a posição.
  const parede = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const mq = window.matchMedia(LARGO);
    const aoMudar = () => setLargo(mq.matches);
    mq.addEventListener("change", aoMudar);
    return () => mq.removeEventListener("change", aoMudar);
  }, []);

  const carregar = useCallback(async () => {
    try {
      const r = await pedir("mural.listar");
      setAnotacoes(r.anotacoes);
      setErro(null);
    } catch (e) {
      setErro(e instanceof FalhaDaApi ? e.message : "Não foi possível ler o mural.");
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  /*
   * Onde o papel novo nasce.
   *
   * A geometria é medida aqui porque é aqui que ela existe: a parede muda de
   * largura com a janela, e a fração que o papel ocupa nela só se sabe olhando
   * as duas. Sem parede na tela — primeira anotação, ou a lista do estreito —
   * não há o que medir, e o servidor escolhe com o tamanho de referência. Na
   * primeira anotação isso não custa nada: não há ninguém para cobrir.
   */
  function posicaoDoPapelNovo(): { x: number; y: number } | null {
    const caixa = parede.current?.getBoundingClientRect();
    const papel = parede.current?.querySelector(".papel")?.getBoundingClientRect();
    if (!caixa || !papel || caixa.width === 0 || caixa.height === 0) return null;
    return proximaPosicaoLivre(anotacoes, {
      largura: papel.width / caixa.width,
      altura: papel.height / caixa.height,
    });
  }

  /*
   * Criar dá retorno **antes** da resposta do servidor (Fase 6A): o botão fica
   * pressionado e desabilitado enquanto a viagem acontece. Otimista de verdade
   * não cabe aqui — o papel nasce com id e posição decididos pelo servidor, e um
   * papel de mentira na parede teria de ser trocado por outro no quadro
   * seguinte, o que pisca mais do que esperar.
   */
  async function criar() {
    if (criando) return;
    setCriando(true);
    setErro(null);
    try {
      const posicao = posicaoDoPapelNovo();
      const r = await pedir("mural.criar", {
        corpo: { texto: "", cor: proximaCor(anotacoes.length), ...(posicao ?? {}) },
      });
      setAnotacoes(r.anotacoes);
    } catch (e) {
      setErro(e instanceof FalhaDaApi ? e.message : "Não foi possível criar a anotação.");
    } finally {
      setCriando(false);
    }
  }

  if (carregando) return null;

  return (
    <section className="mural" aria-labelledby="mural-titulo">
      <div className="mural__topo">
        <h2 className="t-secao" id="mural-titulo">
          Anotações
        </h2>
        <button className="link-mao" type="button" disabled={criando} onClick={() => void criar()}>
          <Icone glifo={IconeCriar} papel="acao" tamanho="p" />
          {criando ? "criando…" : "nova"}
        </button>
      </div>

      {erro && (
        <p className="campo__erro" role="alert">
          {erro}
        </p>
      )}

      {anotacoes.length === 0 ? (
        <p className="t-ajuda">Nada por aqui. Anote o que quer lembrar hoje.</p>
      ) : largo ? (
        <Parede anotacoes={anotacoes} aoMudar={setAnotacoes} aoFalhar={setErro} parede={parede} />
      ) : (
        <Lista anotacoes={anotacoes} aoMudar={setAnotacoes} aoFalhar={setErro} />
      )}
    </section>
  );
}

/* ------------------------------------------------------- tela larga: parede */

/**
 * Um arrasto em curso. Ele nasce **candidato** (`ativo: false`) e só vira
 * arrasto de verdade depois de o ponteiro andar o limiar abaixo.
 *
 * É isso que deixa o papel inteiro ser área de arraste sem tirar o texto de
 * quem quer escrever: parado, o toque é clique e o campo recebe o foco; andando,
 * é arrasto e o papel acompanha. `focar` é o campo do papel que originou o
 * gesto — a parede não conhece o `<textarea>` de cada papel, e não deveria.
 */
type Arrasto = {
  id: string;
  dx: number;
  dy: number;
  origemX: number;
  origemY: number;
  ativo: boolean;
  focar: () => void;
};

/**
 * Quantos pixels o ponteiro precisa andar para o gesto virar arrasto.
 *
 * Não é medida de layout nem regra de negócio: é a tolerância do tremor da mão
 * entre apertar e soltar. Zero faria todo clique arrastar um fio; grande demais
 * faria o papel "grudar" antes de sair.
 */
const LIMIAR_DE_ARRASTO = 4;

function Parede({
  anotacoes,
  aoMudar,
  aoFalhar,
  parede,
}: {
  anotacoes: AnotacaoRapida[];
  aoMudar: (a: AnotacaoRapida[]) => void;
  aoFalhar: (m: string) => void;
  parede: React.RefObject<HTMLDivElement | null>;
}) {
  const arrastando = useRef<Arrasto | null>(null);
  const relogios = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  // Um debounce por anotação. Um só, compartilhado, faria arrastar o segundo
  // papel cancelar a gravação do primeiro — e a posição dele se perderia.
  const gravarPosicao = useCallback(
    (id: string, x: number, y: number) => {
      clearTimeout(relogios.current.get(id));
      relogios.current.set(
        id,
        setTimeout(() => {
          void pedir("mural.mover", { params: { id }, corpo: { x, y } }).catch(() =>
            aoFalhar("A posição não foi gravada."),
          );
        }, 400),
      );
    },
    [aoFalhar],
  );

  // O flush do desmontar: sair da home no meio de um arrasto perderia a última
  // posição, que é justamente a que a pessoa escolheu.
  useEffect(() => {
    const pendentes = relogios.current;
    return () => {
      for (const t of pendentes.values()) clearTimeout(t);
    };
  }, []);

  useEffect(() => {
    function aoMover(e: PointerEvent) {
      const a = arrastando.current;
      const caixa = parede.current?.getBoundingClientRect();
      if (!a || !caixa) return;

      // Antes do limiar não é arrasto ainda: é um clique que pode virar um.
      // Sem isto, tocar o papel para escrever moveria o papel um pixel e o
      // clique nunca chegaria ao texto.
      if (!a.ativo) {
        if (Math.hypot(e.clientX - a.origemX, e.clientY - a.origemY) < LIMIAR_DE_ARRASTO) return;
        a.ativo = true;
      }

      // Fração da caixa, presa entre 0 e 1: papel arrastado para fora da parede
      // ficaria gravado num lugar que nenhuma largura de janela alcança.
      const x = Math.min(Math.max((e.clientX - caixa.left - a.dx) / caixa.width, 0), 1);
      const y = Math.min(Math.max((e.clientY - caixa.top - a.dy) / caixa.height, 0), 1);
      aoMudar(anotacoes.map((n) => (n.id === a.id ? { ...n, x, y } : n)));
      gravarPosicao(a.id, x, y);
    }
    function aoSoltar() {
      const a = arrastando.current;
      arrastando.current = null;
      // Soltou sem passar do limiar: era um clique. O `pointerdown` recusou o
      // foco nativo para poder arrastar por cima do texto, então quem entrega o
      // cursor ao campo é este ponto — e é ele que liga o modo de edição.
      if (a && !a.ativo) a.focar();
    }
    window.addEventListener("pointermove", aoMover);
    window.addEventListener("pointerup", aoSoltar);
    window.addEventListener("pointercancel", aoSoltar);
    return () => {
      window.removeEventListener("pointermove", aoMover);
      window.removeEventListener("pointerup", aoSoltar);
      window.removeEventListener("pointercancel", aoSoltar);
    };
  }, [anotacoes, aoMudar, gravarPosicao]);

  return (
    <div className="mural__parede" ref={parede}>
      {anotacoes.map((a) => (
        <Papel
          key={a.id}
          anotacao={a}
          aoMudar={aoMudar}
          aoFalhar={aoFalhar}
          aoPegar={(e, focar) => {
            const alvo = (e.currentTarget as HTMLElement).getBoundingClientRect();
            arrastando.current = {
              id: a.id,
              dx: e.clientX - alvo.left,
              dy: e.clientY - alvo.top,
              origemX: e.clientX,
              origemY: e.clientY,
              ativo: false,
              focar,
            };
          }}
          estilo={{ "--papel-x": `${a.x * 100}%`, "--papel-y": `${a.y * 100}%` } as React.CSSProperties}
        />
      ))}
    </div>
  );
}

/* ----------------------------------------------------- tela estreita: lista */

function Lista({
  anotacoes,
  aoMudar,
  aoFalhar,
}: {
  anotacoes: AnotacaoRapida[];
  aoMudar: (a: AnotacaoRapida[]) => void;
  aoFalhar: (m: string) => void;
}) {
  async function mover(indice: number, direcao: -1 | 1) {
    const alvo = indice + direcao;
    if (alvo < 0 || alvo >= anotacoes.length) return;
    const nova = [...anotacoes];
    [nova[indice], nova[alvo]] = [nova[alvo]!, nova[indice]!];
    aoMudar(nova);
    try {
      // A lista inteira, e não o par de índices: a ordem final é a que a tela
      // mostrou, e não o resultado de trocas que podem ter cruzado com outra aba.
      const r = await pedir("mural.reordenar", { corpo: { ids: nova.map((n) => n.id) } });
      aoMudar(r.anotacoes);
    } catch (e) {
      aoFalhar(e instanceof FalhaDaApi ? e.message : "A ordem não foi gravada.");
    }
  }

  return (
    <ul className="mural__lista">
      {anotacoes.map((a, i) => (
        <li key={a.id}>
          <Papel
            anotacao={a}
            aoMudar={aoMudar}
            aoFalhar={aoFalhar}
            controles={
              <>
                <button
                  type="button"
                  className="papel__mover"
                  aria-label="Subir na lista"
                    title="Subir na lista"
                  disabled={i === 0}
                  onClick={() => void mover(i, -1)}
                >
                  <Icone glifo={IconeVoltar} papel="acao" tamanho="p" />
                </button>
                <button
                  type="button"
                  className="papel__mover"
                  aria-label="Descer na lista"
                    title="Descer na lista"
                  disabled={i === anotacoes.length - 1}
                  onClick={() => void mover(i, 1)}
                >
                  <Icone glifo={IconeSeguir} papel="acao" tamanho="p" />
                </button>
              </>
            }
          />
        </li>
      ))}
    </ul>
  );
}

/* -------------------------------------------------------------- o papel --- */

export function Papel({
  anotacao,
  aoMudar,
  aoFalhar,
  aoPegar,
  estilo,
  controles,
}: {
  anotacao: AnotacaoRapida;
  aoMudar: (a: AnotacaoRapida[]) => void;
  aoFalhar: (m: string) => void;
  aoPegar?: (e: React.PointerEvent, focar: () => void) => void;
  estilo?: React.CSSProperties;
  controles?: React.ReactNode;
}) {
  const [texto, setTexto] = useState(anotacao.texto);
  const [editando, setEditando] = useState(false);
  // Um estado só serve os dois ponteiros: no mouse ele acende ao passar por
  // cima, no toque ele é o primeiro dos dois toques.
  const [pontaAberta, setPontaAberta] = useState(false);
  const campo = useRef<HTMLTextAreaElement>(null);
  const relogio = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Conteúdo vindo de fora — outra aba, ou a resposta de uma reordenação.
  useEffect(() => setTexto(anotacao.texto), [anotacao.texto]);

  const gravar = useCallback(
    (novoTexto: string, cor: CorDoMural) => {
      if (relogio.current) clearTimeout(relogio.current);
      relogio.current = setTimeout(() => {
        void pedir("mural.editar", { params: { id: anotacao.id }, corpo: { texto: novoTexto, cor } })
          .then((r) => aoMudar(r.anotacoes))
          .catch(() => aoFalhar("A anotação não foi gravada."));
      }, 800);
    },
    [anotacao.id, aoMudar, aoFalhar],
  );

  // O mesmo flush do editor do caderno (RN-44, no espírito): debounce sozinho
  // perde a última coisa digitada, que é a que a pessoa acabou de escrever.
  useEffect(() => {
    return () => {
      if (relogio.current) clearTimeout(relogio.current);
    };
  }, []);

  // Quase todo o papel arrasta, e quem decide não é uma região desenhada: é o
  // estado do texto. Fora de edição o papel inteiro é alça; com o texto em
  // edição o arrasto desliga e o gesto volta a ser seleção de texto.
  const arrastavel = Boolean(aoPegar) && !editando;

  const descartar = () => {
    void pedir("mural.excluir", { params: { id: anotacao.id } })
      .then((r) => aoMudar(r.anotacoes))
      .catch(() => aoFalhar("Não foi possível descartar."));
  };

  return (
    <article
      className={
        `papel ${anotacao.cor}` +
        (arrastavel ? " papel--arrastavel" : "") +
        (pontaAberta ? " papel--ponta-aberta" : "")
      }
      style={estilo}
      onPointerDown={(e) => {
        // Encostar em qualquer outro lugar do papel devolve a ponta ao lugar.
        // É o que fecha o descarte no toque, onde não existe "sair de cima".
        if (!(e.target as HTMLElement).closest(".papel__ponta")) setPontaAberta(false);
        if (!arrastavel) return;
        // Botão não arrasta: descartar e reordenar continuam sendo alvos.
        if ((e.target as HTMLElement).closest("button")) return;
        // Recusa o foco nativo do `<textarea>`, que acontece já no
        // `pointerdown`. Sem isto, apertar sobre o texto para arrastar
        // ligaria a edição no mesmo instante e desligaria o arrasto. Quem
        // devolve o foco é a parede, se o gesto não passar do limiar.
        e.preventDefault();
        aoPegar!(e, () => campo.current?.focus());
      }}
    >
      {/*
        A cor não aparece em alvo nenhum: ela é sorteada na criação (ADR 018).
        O que se manda de volta ao gravar é a cor que o papel já tem — mudar
        texto não é mudar cor.

        O texto é o **primeiro** filho: num papel se escreve a partir do topo. A
        linha de controles que morava aqui em cima era resquício da alça e dos
        quatro alvos de cor, os dois já removidos, e o que ela fazia era empurrar
        o texto 44px para baixo.
      */}
      <textarea
        ref={campo}
        className="papel__texto"
        value={texto}
        maxLength={500}
        rows={4}
        aria-label="Texto da anotação"
        onFocus={() => setEditando(true)}
        onBlur={() => setEditando(false)}
        onChange={(e) => {
          setTexto(e.target.value);
          gravar(e.target.value, anotacao.cor);
        }}
      />

      {/* Só a lista estreita tem reordenação; na parede a ordem é a posição. */}
      {controles && <div className="papel__pe">{controles}</div>}

      {/*
        O descarte mora **sob a ponta dobrada** (ADR 023).

        No mouse, `pointerenter` descola a ponta e o botão aparece — o clique
        seguinte descarta, como sempre foi. No toque são **dois toques**: o
        primeiro descola, o segundo descarta. A guarda de `pointerType` é o que
        separa os dois: toque também dispara `pointerenter`, e sem ela a ponta
        abriria no mesmo gesto que descarta.

        O primeiro toque vale **só aqui**, e não no papel inteiro: quem tem a
        região é o botão, do tamanho do alvo mínimo.

        **O desenho e o alvo são dois elementos**, e não um. O glifo mora atrás
        do papel, e quem o descobre é o recorte da ponta; o botão fica na frente
        de tudo, transparente, para o toque nunca cair no texto que passa por
        baixo dele. Juntos os dois seriam ou um alvo inalcançável, ou um ícone
        boiando por cima da folha.
      */}
      <div className="papel__ponta" aria-hidden="true">
        <Icone glifo={IconeExcluir} papel="acao" tamanho="p" />
      </div>
      <button
        type="button"
        className="papel__excluir"
        aria-label="Descartar a anotação"
      title="Descartar a anotação"
        onPointerEnter={(e) => {
          if (e.pointerType === "mouse") setPontaAberta(true);
        }}
        onPointerLeave={(e) => {
          if (e.pointerType === "mouse") setPontaAberta(false);
        }}
        onFocus={() => setPontaAberta(true)}
        onBlur={() => setPontaAberta(false)}
        onClick={() => {
          if (!pontaAberta) {
            setPontaAberta(true);
            return;
          }
          descartar();
        }}
      />
    </article>
  );
}
